import { computed, inject, Injectable, signal, effect } from '@angular/core';

import { StorageService } from '../storage/storage.service';
import { type Product } from '../products/product.interface';
import { type CartItem } from './cart-item.interface';

@Injectable({
  providedIn: 'root',
})
export class CartService {
  private readonly CART_STORAGE_KEY = 'shopping-cart';
  private readonly storage = inject(StorageService);

  readonly #cartItems = signal<CartItem[]>(this.loadCart());
  readonly #isCartOpen = signal(false);

  // Public reactive state
  readonly items = this.#cartItems.asReadonly();
  readonly isOpen = this.#isCartOpen.asReadonly();

  readonly totalItems = computed(() =>
    this.#cartItems().reduce((total, item) => total + item.quantity, 0),
  );

  readonly totalAmount = computed(() =>
    this.#cartItems().reduce(
      (total, item) => total + (item.product.priceWithTax ?? 0) * item.quantity,
      0,
    ),
  );

  constructor() {
    // Automatically sync cart to localStorage whenever it changes
    effect(() => {
      this.storage.setItem(this.CART_STORAGE_KEY, this.#cartItems());
    });
  }

  private loadCart(): CartItem[] {
    const savedCart = this.storage.getItem<CartItem[]>(this.CART_STORAGE_KEY);
    return savedCart || [];
  }

  addItem(product: Product): void {
    const existingItem = this.#cartItems().find(
      (item) => item.product.CodProd === product.CodProd,
    );

    if (existingItem) {
      if (existingItem.quantity >= product.Existen) return; // Stock limit reached

      this.#cartItems.update((items) =>
        items.map((item) =>
          item.product.CodProd === product.CodProd
            ? { ...item, quantity: item.quantity + 1 }
            : item,
        ),
      );

      return;
    }

    if (product.Existen < 1) return; // Prevent adding out-of-stock items

    this.#cartItems.update((items) => [...items, { product, quantity: 1 }]);
  }

  removeItem(productId: string): void {
    this.#cartItems.update((items) =>
      items.filter((item) => item.product.CodProd !== productId),
    );
  }

  updateQuantity(productId: string, quantity: number, maxStock?: number): void {
    if (quantity <= 0) {
      this.removeItem(productId);
      return;
    }

    // Clamp quantity to maxStock if provided
    const finalQuantity =
      maxStock !== undefined ? Math.min(quantity, maxStock) : quantity;

    this.#cartItems.update((items) =>
      items.map((item) =>
        item.product.CodProd === productId
          ? { ...item, quantity: finalQuantity }
          : item,
      ),
    );
  }

  clearCart(): void {
    this.#cartItems.set([]);
  }

  toggleCart(): void {
    this.#isCartOpen.update((open) => !open);
  }

  setCartOpen(open: boolean): void {
    this.#isCartOpen.set(open);
  }
}
